import React from 'react';
import { Flex } from '@fullstory/components';
import { classNames } from '@fullstory/util/fmt';
import css from './index.module.scss';

interface CarouselProps {
  className?: string;
  children: React.ReactNode;
}

interface CarouselState {
  index: number; // Index of the currently centered card
}

class Carousel extends React.Component<CarouselProps, CarouselState> {
  private readonly scrollRef = React.createRef<HTMLDivElement>();
  private readonly cardRef = React.createRef<Card>();

  constructor(props: CarouselProps) {
    super(props);
    this.state = { index: 0 };
  }

  componentDidUpdate(_: Readonly<CarouselProps>, prevState: Readonly<CarouselState>) {
    if (!this.scrollRef || !this.scrollRef.current || !this.cardRef || !this.cardRef.current) {
      return;
    }

    const scrollRect = this.scrollRef.current.getBoundingClientRect();
    const cardRect = this.cardRef.current.getBoundingClientRect();

    if (cardRect == null) return;

    this.scrollRef.current.scroll({
      left:
        this.scrollRef.current.scrollLeft +
        cardRect.left -
        scrollRect.left -
        Math.abs(scrollRect.width - cardRect.width) / 2,
      behavior: 'smooth',
    });
  }

  render() {
    const className = classNames(css.carousel, this.props.className);
    return (
      <Flex
        data-component="Carousel"
        className={className}
        alignItems="stretch"
        justifyContent="flex-start"
        ref={this.scrollRef}
      >
        {React.Children.map(this.props.children, (child, idx) => {
          const ref = idx == this.state.index ? this.cardRef : undefined;
          return (
            <Card ref={ref} onClick={() => this.handleCardClick(idx)}>
              {child}
            </Card>
          );
        })}
      </Flex>
    );
  }

  private handleCardClick = (i: number) => {
    this.setState({ index: i });
  };
}

interface CardProps {
  onClick: () => void;
  children: React.ReactNode;
  ref?: React.RefObject<Card>;
}

class Card extends React.PureComponent<CardProps> {
  private ref = React.createRef<HTMLDivElement>();

  render() {
    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
      <div data-component="Card" className={css.card} onClick={this.props.onClick}>
        <Flex className={css.content} ref={this.ref}>
          {this.props.children}
        </Flex>
      </div>
    );
  }

  getBoundingClientRect(): ClientRect | null {
    return this.ref && this.ref.current ? this.ref.current.getBoundingClientRect() : null;
  }
}

export { Carousel };
